import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for video processing
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // You can add auth tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.response) {
      // Server responded with error
      switch (error.response.status) {
        case 400:
          error.message = 'Bad request. Please check your input.';
          break;
        case 401:
          error.message = 'Unauthorized. Please login.';
          break;
        case 404:
          error.message = 'Resource not found.';
          break;
        case 500:
          error.message = 'Server error. Please try again later.';
          break;
        default:
          error.message = `Error ${error.response.status}: ${error.response.data?.detail || 'Unknown error'}`;
      }
    } else if (error.request) {
      // Request made but no response
      error.message = 'Network error. Please check your connection.';
    } else {
      // Something else happened
      error.message = error.message || 'An unexpected error occurred.';
    }
    
    return Promise.reject(error);
  }
);

// Analysis API
export const analyzeVideo = async (formData, onProgress) => {
  const response = await api.post('/api/analyze', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    },
  });
  return response.data;
};

export const getAnalysis = async (analysisId) => {
  const response = await api.get(`/api/analyses/${analysisId}`);
  return response.data;
};

export const getAnalyses = async (limit = 10, skip = 0) => {
  const response = await api.get('/api/analyses', {
    params: { limit, skip },
  });
  return response.data;
};

export const deleteAnalysis = async (analysisId) => {
  const response = await api.delete(`/api/analyses/${analysisId}`);
  return response.data;
};

export const getStats = async () => {
  const response = await api.get('/api/stats');
  return response.data;
};

export const analyzeBatch = async (videos, calibrationDistance = 20.12) => {
  const formData = new FormData();
  videos.forEach((video) => {
    formData.append('videos', video);
  });
  formData.append('calibration_distance', calibrationDistance);

  const response = await api.post('/api/batch/analyze', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getVideoMetadata = async (videoFile) => {
  const formData = new FormData();
  formData.append('video', videoFile);
  
  const response = await api.post('/api/video/metadata', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Health check
export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw new Error('API is not available');
  }
};

export default api;